import type { Vec3 } from "../entity/types";
import { VOXELS_PER_METRE } from "../world/constants";
import type { WorldView } from "../world/types";

/**
 * Player collision volume, in voxels. Written in metres and converted once —
 * the whole world is eight voxels to the metre, so a bare 1.8 here would be a
 * 22 cm player.
 */
export const PLAYER_HEIGHT = 1.8 * VOXELS_PER_METRE;
export const PLAYER_WIDTH = 0.6 * VOXELS_PER_METRE;
/** Eye sits a little below the crown, as a head does. */
export const PLAYER_EYE_HEIGHT = 1.62 * VOXELS_PER_METRE;

/**
 * A step this tall is climbed without jumping, so the micro-voxel ground —
 * which is bumpy by design, see SURFACE_EROSION — does not catch every stride.
 * Half a metre is four voxels of erosion plus margin.
 */
export const STEP_HEIGHT = 0.55 * VOXELS_PER_METRE;

export interface PhysicsTuning {
  /** Downward acceleration, voxels per second squared. */
  readonly gravity: number;
  /** Ground speed, voxels per second. */
  readonly walkSpeed: number;
  readonly sprintSpeed: number;
  /** Upward launch speed, voxels per second. */
  readonly jumpSpeed: number;
  /** Fraction of horizontal speed retained per second while airborne. */
  readonly airControl: number;
  /** Terminal downward speed, voxels per second. */
  readonly terminalVelocity: number;
}

export const DEFAULT_TUNING: Readonly<PhysicsTuning> = Object.freeze({
  gravity: 26 * VOXELS_PER_METRE,
  walkSpeed: 4.3 * VOXELS_PER_METRE,
  sprintSpeed: 6.2 * VOXELS_PER_METRE,
  // Clears a one-metre ledge, which is the tallest thing worth stepping onto
  // without building. v = sqrt(2 g h) with a little margin.
  jumpSpeed: 8.2 * VOXELS_PER_METRE,
  airControl: 0.35,
  terminalVelocity: 55 * VOXELS_PER_METRE,
});

/**
 * Physics integrates in place every frame, so it needs a mutable vector. `Vec3`
 * in the entity layer is readonly on purpose — the reducer hands out snapshots
 * — and a mutable one still satisfies it wherever a reader is expected.
 */
export interface MutableVec3 {
  x: number;
  y: number;
  z: number;
}

/** Feet-centre position, so `position.y` is the standing surface. */
export interface Body {
  position: MutableVec3;
  velocity: MutableVec3;
  onGround: boolean;
}

export interface MoveInput {
  /** -1 back, +1 forward. */
  readonly forward: number;
  /** -1 left, +1 right. */
  readonly strafe: number;
  readonly jump: boolean;
  readonly sprint: boolean;
  /** Radians, matching the render camera's yaw convention. */
  readonly yaw: number;
}

export function createBody(position: Vec3): Body {
  return {
    position: { ...position },
    velocity: { x: 0, y: 0, z: 0 },
    onGround: false,
  };
}

/**
 * An unloaded sub-chunk counts as solid. The alternative — treating it as air —
 * drops the player through the floor whenever streaming falls behind, which is
 * a far worse failure than briefly refusing to move.
 */
function blocked(world: WorldView, x: number, y: number, z: number): boolean {
  if (!world.isLoaded(x, y, z)) return true;
  return world.isSolid(x, y, z);
}

/**
 * True when every voxel the body occupies, plus the voxel under its feet, is in
 * a resident sub-chunk. Callers use this to tell "standing in mid-air" from
 * "standing where the terrain has not arrived yet".
 */
export function bodyChunksLoaded(world: WorldView, position: Vec3): boolean {
  const half = PLAYER_WIDTH / 2;
  const minY = Math.floor(position.y) - 1;
  const maxY = Math.floor(position.y + PLAYER_HEIGHT);
  for (let y = minY; y <= maxY; y += 1) {
    for (const z of [position.z - half, position.z + half]) {
      for (const x of [position.x - half, position.x + half]) {
        if (!world.isLoaded(Math.floor(x), y, Math.floor(z))) return false;
      }
    }
  }
  return true;
}

/** True when the body's AABB overlaps any solid voxel. */
export function intersectsSolid(world: WorldView, position: Vec3): boolean {
  const half = PLAYER_WIDTH / 2;
  const minX = Math.floor(position.x - half);
  const maxX = Math.floor(position.x + half);
  const minY = Math.floor(position.y);
  const maxY = Math.floor(position.y + PLAYER_HEIGHT);
  const minZ = Math.floor(position.z - half);
  const maxZ = Math.floor(position.z + half);
  for (let y = minY; y <= maxY; y += 1) {
    for (let z = minZ; z <= maxZ; z += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        if (blocked(world, x, y, z)) return true;
      }
    }
  }
  return false;
}

/**
 * Advances one axis and stops at the contact point.
 *
 * Bisection rather than arithmetic snapping: the player is 4.8 voxels wide, so
 * its bounds are not voxel-aligned and there is no clean face to snap to.
 * Twelve halvings resolve contact to under a thousandth of a voxel, which is
 * well past anything visible, and the same code serves all three axes.
 *
 * @returns true when the move was stopped short by geometry.
 */
function moveAxis(world: WorldView, position: MutableVec3, axis: "x" | "y" | "z", delta: number): boolean {
  if (delta === 0) return false;
  const start = position[axis];
  position[axis] = start + delta;
  if (!intersectsSolid(world, position)) return false;
  let free = 0;
  let hit = 1;
  for (let iteration = 0; iteration < 12; iteration += 1) {
    const middle = (free + hit) / 2;
    position[axis] = start + delta * middle;
    if (intersectsSolid(world, position)) hit = middle;
    else free = middle;
  }
  position[axis] = start + delta * free;
  return true;
}

/**
 * Horizontal move with an automatic step up.
 *
 * Tried flat first; only if that is blocked does it retry lifted by
 * STEP_HEIGHT and settle back down. Without this the eroded micro-voxel surface
 * stops the player on every one-voxel bump.
 */
function moveHorizontal(world: WorldView, position: MutableVec3, dx: number, dz: number, onGround: boolean): {
  readonly hitX: boolean;
  readonly hitZ: boolean;
} {
  const before = { ...position };
  const hitX = moveAxis(world, position, "x", dx);
  const hitZ = moveAxis(world, position, "z", dz);
  if ((!hitX && !hitZ) || !onGround) return { hitX, hitZ };

  const flat = { ...position };
  const stepped = { ...before };
  if (moveAxis(world, stepped, "y", STEP_HEIGHT)) return { hitX, hitZ };
  const steppedHitX = moveAxis(world, stepped, "x", dx);
  const steppedHitZ = moveAxis(world, stepped, "z", dz);
  moveAxis(world, stepped, "y", -STEP_HEIGHT);

  // Only keep the step if it actually bought horizontal distance.
  const flatGain = Math.hypot(flat.x - before.x, flat.z - before.z);
  const stepGain = Math.hypot(stepped.x - before.x, stepped.z - before.z);
  if (stepGain <= flatGain + 1e-6) return { hitX, hitZ };
  position.x = stepped.x;
  position.y = stepped.y;
  position.z = stepped.z;
  return { hitX: steppedHitX, hitZ: steppedHitZ };
}

/**
 * Advances the body one frame.
 *
 * `seconds` must be small — callers clamp it — because collision is resolved
 * per axis against the state after the move, not swept through it.
 */
export function stepBody(
  world: WorldView,
  body: Body,
  input: MoveInput,
  seconds: number,
  tuning: PhysicsTuning = DEFAULT_TUNING,
): void {
  if (seconds <= 0) return;

  // Terrain under the body has not streamed in yet. Hold position entirely:
  // gravity here would drop the player through a floor that does not exist
  // yet, and the embedded-escape below would launch them into the sky, since
  // unloaded reads as solid.
  if (!bodyChunksLoaded(world, body.position)) return;

  // A body that starts embedded — spawned inside terrain, or terrain placed
  // into it — climbs out rather than being frozen by the bisection below.
  if (intersectsSolid(world, body.position)) {
    body.position.y += Math.max(1, tuning.walkSpeed * seconds);
    body.velocity.y = 0;
    body.onGround = false;
    return;
  }

  const speed = input.sprint ? tuning.sprintSpeed : tuning.walkSpeed;
  const magnitude = Math.hypot(input.forward, input.strafe);
  let wishX = 0;
  let wishZ = 0;
  if (magnitude > 0) {
    // Matches the camera basis in the renderer: forward is -Z at yaw 0.
    const forwardX = -Math.sin(input.yaw);
    const forwardZ = -Math.cos(input.yaw);
    const rightX = Math.cos(input.yaw);
    const rightZ = -Math.sin(input.yaw);
    const normalizedForward = input.forward / magnitude;
    const normalizedStrafe = input.strafe / magnitude;
    wishX = (forwardX * normalizedForward + rightX * normalizedStrafe) * speed;
    wishZ = (forwardZ * normalizedForward + rightZ * normalizedStrafe) * speed;
  }

  if (body.onGround) {
    body.velocity.x = wishX;
    body.velocity.z = wishZ;
    if (input.jump) {
      body.velocity.y = tuning.jumpSpeed;
      body.onGround = false;
    }
  } else {
    // Airborne steering is damped, so a jump commits to its arc.
    body.velocity.x += (wishX - body.velocity.x) * Math.min(1, tuning.airControl * seconds * 10);
    body.velocity.z += (wishZ - body.velocity.z) * Math.min(1, tuning.airControl * seconds * 10);
  }

  body.velocity.y = Math.max(-tuning.terminalVelocity, body.velocity.y - tuning.gravity * seconds);

  const { hitX, hitZ } = moveHorizontal(
    world,
    body.position,
    body.velocity.x * seconds,
    body.velocity.z * seconds,
    body.onGround,
  );
  if (hitX) body.velocity.x = 0;
  if (hitZ) body.velocity.z = 0;

  const falling = body.velocity.y <= 0;
  const hitY = moveAxis(world, body.position, "y", body.velocity.y * seconds);
  if (hitY) {
    body.onGround = falling;
    body.velocity.y = 0;
  } else {
    body.onGround = false;
  }

  // Standing still on a surface still needs a ground probe, or a body that
  // never moves vertically reports airborne and refuses to jump.
  if (!body.onGround && falling) {
    const probe = { ...body.position };
    if (moveAxis(world, probe, "y", -0.05)) body.onGround = true;
  }
}

/** Camera position for a body: eye height above the feet. */
export function eyePosition(body: Body): Vec3 {
  return { x: body.position.x, y: body.position.y + PLAYER_EYE_HEIGHT, z: body.position.z };
}
